import dayjs from 'dayjs';
import type { MultiPolygon, Polygon } from 'geojson';

import type { AlphaEarthIndex } from '@/features/map/types/map.types';

export function generateFloodJobId(): string {
  return `JOB-${dayjs().format('YYYYMMDD')}-${Math.floor(Math.random() * 1000)
    .toString()
    .padStart(3, '0')}`;
}

/** Baseline vs crisis window for SAR previews (labels + Alpha Earth `year`). */
export type SarFloodJobSceneRole = 'baseline' | 'crisis';

/** Hai cách tải RGB trong gallery SAR: true color S2 vs band embedding mặc định. */
export type SarRgbBandProfile = 'sentinel2' | 'embedding';

/** Một lớp ảnh GEE/Alpha Earth (chỉ số khác nhau cho cùng khoảng thời gian). */
export interface SarFloodJobGeeFrame {
  index: AlphaEarthIndex;
  /** Chỉ khi `index === 'RGB'`: phân biệt true color và embedding. */
  rgbProfile?: SarRgbBandProfile;
  imageUrl?: string;
  caption?: string;
}

/**
 * One preview frame: khoảng ngày hiển thị + năm đại diện cho API tiles (Alpha Earth).
 */
export interface SarFloodJobSceneSlide {
  role: SarFloodJobSceneRole;
  /** Hiển thị: DD/MM/YYYY hoặc DD/MM/YYYY – DD/MM/YYYY */
  periodLabel: string;
  /** Ngày tham chiếu tiles Alpha Earth (YYYY-MM-DD), thường cuối khoảng Baseline/Crisis */
  referenceDate: string;
  /** Ảnh đại diện (thường RGB) — tương thích ngược */
  imageUrl?: string;
  caption?: string;
  /** Nhiều chỉ số sau hydrate (RGB, NDVI, …) */
  geeFrames?: SarFloodJobGeeFrame[];
}

/** Loại tác vụ phân tích AOI từ wizard bản đồ (SAR ngập vs lớp phủ quang học). */
export type SarAoiAnalysisKind = 'sarFlood' | 'landCover';

/**
 * Metadata runtime cho engine mô phỏng job: tổng thời lượng mục tiêu + tỉ lệ mỗi bước.
 * Dựa trên `stageWeights` (tổng = 1) để suy ra step đang chạy theo elapsed / target.
 */
export interface FloodJobRuntime {
  targetDurationMs: number;
  stageWeights: number[];
}

export interface FloodJobStep {
  name: string;
  status: 'pending' | 'running' | 'done' | 'error';
  durationSec?: number;
}

export interface FloodJob {
  id: string;
  /** ID của kết quả lưu trong bảng flood_detection_results (khác với job_id). */
  resultId?: string;
  name: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  progress: number; // 0–100
  aoi: string; // Area of Interest description
  /** Polygon AOI (map-drawn SAR) when applicable */
  aoiPolygon?: Polygon;
  /** km² — map-drawn AOI */
  aoiAreaKm2?: number;
  startedAt: string;
  finishedAt?: string;
  duration?: string;
  submittedBy: string;
  parameters: {
    baseline: string;
    crisis: string;
    baselineEnd?: string;
    crisisEnd?: string;
    polarization: string;
    threshold: number;
    /** Mặc định coi như `sarFlood` khi không có (job mock cũ). */
    analysisKind?: SarAoiAnalysisKind;
  };
  runtime?: FloodJobRuntime;
  results?: {
    floodedAreaKm2: number;
    accuracyScore: number;
    affectedPop?: number;
    /** Polygon ngập ước lượng trong AOI (BE hoặc mock) — overlay tab Viễn thám. */
    floodExtentGeometry?: Polygon | MultiPolygon | null;
    /** Độ sâu tại điểm trung tâm (cm). */
    depthAtPointCm?: number;
    /** Ước tính độ sâu nhỏ nhất (m). */
    estimatedDepthMinM?: number;
    /** Ước tính độ sâu lớn nhất (m). */
    estimatedDepthMaxM?: number;
    /** Phần trăm diện tích bị ảnh hưởng (0-100). */
    affectedAreaPercent?: number;
    /** Chi tiết tác động lớp phủ. */
    landCoverImpacts?: { categoryKey: string; areaHa: number }[];
    /** Hạ tầng lân cận. */
    nearbyInfrastructure?: {
      hospitals: number;
      schools: number;
      emergencyShelters: number;
      fireStations: number;
      policeStations: number;
      evacuationCenters: number;
    };
    /** Giao thông. */
    blockedRoads?: {
      id: string;
      name: string;
      lengthM: number;
      severity: 'blocked' | 'congested' | 'caution';
      alternateRoute: string;
      estimatedDelay: string;
    }[];
    /** Thiệt hại ước tính. */
    damageAssessment?: {
      affectedHouses: number;
      damagedHouses: number;
      destroyedHouses: number;
      economicLossUSD: number;
      agriculturalAreaHa: number;
    };
    /** Diện tích ngập (Ha). */
    floodedAreaHa?: number;
    /** Đo che phủ thực vật (%) — chỉ populate cho `landCover`. */
    vegetationCoverPct?: number;
    /** Preview theo cửa sổ Baseline / Crisis (hydrate Alpha Earth tùy chọn) */
    sceneGallery?: SarFloodJobSceneSlide[];
    sceneGalleryLoading?: boolean;
    /** Đã thử hydrate gallery (tránh lặp vô hạn khi API không trả ảnh). */
    sceneGalleryHydrateDone?: boolean;
  };
  errorMessage?: string;
  steps: FloodJobStep[];
}

/** Stage weights mặc định theo kind (tổng = 1.0). */
export const DEFAULT_STAGE_WEIGHTS: Record<SarAoiAnalysisKind, number[]> = {
  sarFlood: [0.1, 0.35, 0.3, 0.15, 0.1],
  landCover: [0.15, 0.2, 0.4, 0.15, 0.1],
};

/** Thời lượng mục tiêu (ms) cho engine mô phỏng — khớp với ETA hiển thị trong wizard. */
export const DEFAULT_TARGET_DURATION_MS: Record<SarAoiAnalysisKind, number> = {
  sarFlood: 300_000, // 5 minutes target for realistic ETA
  landCover: 60_000,
};

function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/**
 * % tiến độ (0–100) tại thời điểm `nowMs` cho job đang chạy; easing nhẹ để đầu/cuối mượt hơn giữa.
 */
export function computeJobProgressPercent(job: FloodJob, nowMs: number): number {
  if (!job.runtime) return job.progress;
  const startMs = new Date(job.startedAt).getTime();
  if (!Number.isFinite(startMs)) return job.progress;
  const elapsed = Math.max(0, nowMs - startMs);
  const raw = Math.min(1, elapsed / job.runtime.targetDurationMs);
  return Math.round(easeInOutQuad(raw) * 100);
}

/**
 * % hiển thị trên UI khi job đang chạy: mượt theo đồng hồ, không lùi hơn % server.
 */
export function getJobDisplayProgressPercent(job: FloodJob, nowMs: number): number {
  if (job.status !== 'running' || !job.runtime) {
    return Math.min(100, Math.max(0, job.progress));
  }
  const smooth = computeJobProgressPercent(job, nowMs);
  return Math.min(99, Math.max(job.progress, smooth));
}

/**
 * ETA còn lại (ms): đếm ngược theo `nowMs` (mỗi giây), kẹp bởi % server khi BE nhanh hơn dự kiến.
 */
export function computeJobEtaMs(job: FloodJob, nowMs: number): number {
  if (!job.runtime) return 0;
  const startMs = new Date(job.startedAt).getTime();
  if (!Number.isFinite(startMs)) return 0;

  const elapsed = Math.max(0, nowMs - startMs);
  const fromClock = Math.max(0, job.runtime.targetDurationMs - elapsed);
  const serverPct = Math.max(0, Math.min(100, job.progress));
  const fromServerProgress = Math.max(0, job.runtime.targetDurationMs * (1 - serverPct / 100));

  return Math.round(Math.min(fromClock, fromServerProgress));
}

/**
 * Chỉ số step đang chạy dựa trên `stageWeights` cộng dồn và % tiến độ hiện tại.
 * Trả -1 nếu đã hoàn tất tất cả.
 */
export function getRunningStepIndex(stageWeights: number[], percent: number): number {
  if (!stageWeights.length) return -1;
  if (percent >= 100) return -1;
  const p = Math.max(0, Math.min(100, percent)) / 100;
  let acc = 0;
  for (let i = 0; i < stageWeights.length; i += 1) {
    acc += stageWeights[i];
    if (p < acc) return i;
  }
  return stageWeights.length - 1;
}

/**
 * Cập nhật trạng thái từng step theo running index: trước = done, current = running, sau = pending.
 */
export function applyStepStatusByRunningIndex(
  steps: FloodJobStep[],
  runningIndex: number,
): FloodJobStep[] {
  return steps.map((s, i) => {
    if (runningIndex === -1) return { ...s, status: 'done' as const };
    if (i < runningIndex) return { ...s, status: 'done' as const };
    if (i === runningIndex) return { ...s, status: 'running' as const };
    return { ...s, status: 'pending' as const };
  });
}

/**
 * Reset job (mock) để chạy lại từ đầu: cùng tham số, engine tiến độ theo `runtime` mặc định theo kind.
 */
export function floodJobRerunReset(job: FloodJob): FloodJob {
  const kind = job.parameters.analysisKind ?? 'sarFlood';
  const stageWeights = job.runtime?.stageWeights ?? DEFAULT_STAGE_WEIGHTS[kind];
  const targetDurationMs = job.runtime?.targetDurationMs ?? DEFAULT_TARGET_DURATION_MS[kind];
  return {
    ...job,
    status: 'running',
    progress: 0,
    startedAt: dayjs().toISOString(),
    finishedAt: undefined,
    duration: undefined,
    errorMessage: undefined,
    results: undefined,
    runtime: { targetDurationMs, stageWeights },
    steps: job.steps.map((s) => ({
      ...s,
      status: 'pending' as const,
    })),
  };
}

function formatPeriodDay(d: dayjs.Dayjs): string {
  return d.format('DD/MM/YYYY');
}

function appendWindowSlides(
  role: SarFloodJobSceneRole,
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
  windowRangeLabel: string,
  out: SarFloodJobSceneSlide[],
): void {
  if (start.isSame(end, 'day')) {
    out.push({
      role,
      periodLabel: windowRangeLabel,
      referenceDate: start.format('YYYY-MM-DD'),
    });
    return;
  }
  out.push({
    role,
    periodLabel: `${windowRangeLabel} · ${formatPeriodDay(start)}`,
    referenceDate: start.format('YYYY-MM-DD'),
  });
  out.push({
    role,
    periodLabel: `${windowRangeLabel} · ${formatPeriodDay(end)}`,
    referenceDate: end.format('YYYY-MM-DD'),
  });
}

/**
 * Slide Baseline / Crisis từ tham số job — nếu khoảng nhiều ngày: hai mốc (đầu + cuối) mỗi cửa sổ.
 */
export function buildMockSarSceneGalleryFromParameters(
  parameters: FloodJob['parameters'],
): SarFloodJobSceneSlide[] {
  const baselineStart = dayjs(parameters.baseline);
  const baselineEnd = parameters.baselineEnd ? dayjs(parameters.baselineEnd) : baselineStart;
  const crisisStart = dayjs(parameters.crisis);
  const crisisEnd = parameters.crisisEnd ? dayjs(parameters.crisisEnd) : crisisStart;

  const b0 = baselineStart.isValid() ? baselineStart : dayjs();
  const b1 = baselineEnd.isValid() ? baselineEnd : b0;
  const c0 = crisisStart.isValid() ? crisisStart : dayjs();
  const c1 = crisisEnd.isValid() ? crisisEnd : c0;

  const baselineLabel = b0.isSame(b1, 'day')
    ? formatPeriodDay(b0)
    : `${formatPeriodDay(b0)} – ${formatPeriodDay(b1)}`;
  const crisisLabel = c0.isSame(c1, 'day')
    ? formatPeriodDay(c0)
    : `${formatPeriodDay(c0)} – ${formatPeriodDay(c1)}`;

  const slides: SarFloodJobSceneSlide[] = [];
  appendWindowSlides('baseline', b0, b1, baselineLabel, slides);
  appendWindowSlides('crisis', c0, c1, crisisLabel, slides);
  return slides;
}

/** Demo SAR trong FE đã gỡ — danh sách seed rỗng; BE qua `GET /api/flood-jobs`. */
export const MOCK_FLOOD_JOBS: FloodJob[] = [];
